/**
 * OutlookSwitcher component
 * Allows users to switch between different climate outlook periods (2030, 2050, 2100)
 * Migrated from Gatsby legacy application with Redux integration
 */
import { Stack, Button, Typography } from '@mui/material';
import { memo } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import {
  changeOutlook,
  selectOutlookIndex,
  selectOutlooks,
} from '../store/slices/outlookSlice';

/**
 * OutlookSwitcher component for selecting climate outlook periods
 * @param {Object} props - Component props
 * @param {string} props.variant - Visual variant ('none' or 'home')
 * @param {Function} props.onChange - Callback fired when outlook changes
 * @param {Object} props.sx - Material-UI sx styling prop
 * @returns {React.ReactElement} Outlook switcher component
 */
const OutlookSwitcher = ({
  variant = 'none',
  onChange = () => {},
  sx = {},
  ...props
}) => {
  const outlookIndex = useSelector(selectOutlookIndex);
  const outlooks = useSelector(selectOutlooks);
  const dispatch = useDispatch();

  const isHome = variant === 'home';

  /**
   * Handle click on an outlook button
   * @param {number} index - Index of the selected outlook
   * @returns {Function} Click handler function
   */
  const handleClick = (index) => () => {
    dispatch(changeOutlook({ index }));
    onChange?.(index);
  };

  // Customize styling based on variant
  const customSx = {};
  if (isHome) {
    customSx.fontSize = { xs: '1.2em', md: '1.6em' };
  }

  return (
    <Stack
      direction="row"
      justifyContent={isHome ? 'flex-end' : 'space-between'}
      alignItems="center"
      spacing={2}
      sx={{ mb: 2, ...sx }}
      {...props}
    >
      {isHome && (
        <Typography
          variant="h5"
          sx={{
            textTransform: 'uppercase',
            mr: 'auto !important',
            ml: 'auto !important',
            typography: { xs: 'h6', sm: 'h4' },
          }}
          component="div"
        >
          d'ici
        </Typography>
      )}

      {outlooks.map(({ value, rwl }, index) => {
        const active = outlookIndex === index;
        const localSx = { ...customSx };

        // Apply variant-specific styling
        if (isHome) {
          localSx.bgcolor = active ? '#00c9f8' : 'white';
          localSx.color = active ? 'white' : 'secondary.main';
          localSx[':hover'] = {
            color: 'white',
            bgcolor: 'primary.main'
          };
        }

        return (
          <Button
            key={rwl}
            variant="contained"
            onClick={handleClick(index)}
            sx={{
              bgcolor: active ? 'primary.main' : '#aaa',
              borderRadius: 20,
              fontSize: '1em',
              position: 'relative',
              ...localSx,
            }}
          >
            {value}
          </Button>
        );
      })}
    </Stack>
  );
};

OutlookSwitcher.displayName = 'OutlookSwitcher';

// Optimize with memo for performance
export default memo(OutlookSwitcher);
